import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  ImageBackground,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import { addDoc, collection, doc, getDoc, getDocs, limit, query, serverTimestamp, where } from 'firebase/firestore';
import { showMessage } from 'react-native-flash-message';
import { Colors, Radius, Shadow, Spacing, Typography } from '../theme';
import type { RootStackParamList } from '../types';
import { auth, DEFAULT_COVER, FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import { TONYTE_PROFILE_IMAGE, getUserDisplayName, getUserProfile, userProfileExists, type PublicUserProfile } from '../utils/userProfiles';
import { blockUserFromPublicProfile, getBlockedUserPath } from '../utils/blocking';
import { AuraProfileBadge, useTodaysAura } from '../utils/todaysAura';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import { getVibePairKey, sendVibeRequest, useProfile } from '../hooks/useProfile';
import { getSharedInterests, unvibeUsers } from '../hooks/useVibes';
import { usePairVibeGameScore } from '../utils/vibeGameScore';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { BUILT_IN_INTEREST_KEYS, getInterestTranslationKey } from '../data/interests';
import { useI18n } from '../i18n';

type Props = NativeStackScreenProps<RootStackParamList, 'PublicProfile'>;

const REPORT_REASONS = [
  { id: 'Spam', key: 'publicProfile.reasonSpam' },
  { id: 'Harassment', key: 'publicProfile.reasonHarassment' },
  { id: 'Fake profile', key: 'publicProfile.reasonFake' },
  { id: 'Inappropriate content', key: 'publicProfile.reasonContent' },
  { id: 'Scam or unsafe behavior', key: 'publicProfile.reasonScam' },
  { id: 'Other', key: 'publicProfile.reasonOther' },
] as const;

const ProfileImage: React.FC<{ uri?: string; style: any }> = ({ uri, style }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [uri]);

  return (
    <ImageBackground
      source={uri && !failed ? { uri } : TONYTE_PROFILE_IMAGE}
      style={style}
      imageStyle={styles.profileImage}
      resizeMode="cover"
      onError={() => setFailed(true)}
    />
  );
};

async function loadCounts(userId: string, t: (key: string) => string) {
  const [createdSnap, hostedSnap, createdPlansSnap, hostedPlansSnap] = await Promise.all([
    getDocs(query(collection(firestore, FIRESTORE_COLLECTIONS.activities), where('createdBy', '==', userId), limit(80))),
    getDocs(query(collection(firestore, FIRESTORE_COLLECTIONS.activities), where('hostId', '==', userId), limit(80))),
    getDocs(query(
      collection(firestore, FIRESTORE_COLLECTIONS.plans),
      where('createdBy', '==', userId),
      where('visibility', '==', 'public'),
      limit(80),
    )),
    getDocs(query(
      collection(firestore, FIRESTORE_COLLECTIONS.plans),
      where('hostId', '==', userId),
      where('visibility', '==', 'public'),
      limit(80),
    )),
  ]);

  const eventIds = new Set<string>();
  createdSnap.docs.forEach(item => {
    if (item.data().entryType !== 'community_plan') eventIds.add(item.id);
  });
  hostedSnap.docs.forEach(item => {
    if (item.data().entryType !== 'community_plan') eventIds.add(item.id);
  });

  const publicEvents = [...createdSnap.docs, ...hostedSnap.docs]
    .filter((item, index, items) => item.data().entryType !== 'community_plan' && items.findIndex(other => other.id === item.id) === index)
    .slice(0, 4)
    .map(item => ({ id: item.id, title: String(item.data().title || item.data().name || t('publicProfile.eventFallback')) }));
  const planDocs = [...createdPlansSnap.docs, ...hostedPlansSnap.docs]
    .filter((item, index, items) => items.findIndex(other => other.id === item.id) === index);
  const publicPlans = planDocs
    .slice(0, 4)
    .map(item => ({ id: item.id, title: String(item.data().title || item.data().name || t('publicProfile.planFallback')) }));

  return { eventsCount: eventIds.size, plansCount: planDocs.length, publicEvents, publicPlans };
}

const PublicUserProfileScreen: React.FC<Props> = ({ route }) => {
  const {
    userId,
    chatId = '',
    name: routeName = '',
    sharedInterests: routeSharedInterests,
    vibeStatus: initialVibeStatus = 'none',
  } = route.params;
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [counts, setCounts] = useState({ eventsCount: 0, plansCount: 0 });
  const [publicActivities, setPublicActivities] = useState<{ events: Array<{ id: string; title: string }>; plans: Array<{ id: string; title: string }> }>({ events: [], plans: [] });
  const [loading, setLoading] = useState(true);
  const [profileUnavailable, setProfileUnavailable] = useState(false);
  const [reportVisible, setReportVisible] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [reporting, setReporting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [vibeStatus, setVibeStatus] = useState(initialVibeStatus);
  const [resolvedChatId, setResolvedChatId] = useState(chatId);
  const [vibeBusy, setVibeBusy] = useState(false);
  const [unvibeVisible, setUnvibeVisible] = useState(false);
  const currentUserId = auth.currentUser?.uid ?? '';
  const { profile: ownProfile } = useProfile(currentUserId || null);
  const todaysAura = useTodaysAura(userId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      userProfileExists(userId),
      getUserProfile(userId),
      loadCounts(userId, t).catch(() => ({
        eventsCount: 0,
        plansCount: 0,
        publicEvents: [],
        publicPlans: [],
      })),
    ])
      .then(([exists, nextProfile, nextCounts]) => {
        if (cancelled) return;
        setProfileUnavailable(!exists);
        setProfile(nextProfile);
        setCounts({
          eventsCount: nextProfile.eventsCount || nextCounts.eventsCount,
          plansCount: nextProfile.plansCount || nextCounts.plansCount,
        });
        setPublicActivities({ events: nextCounts.publicEvents, plans: nextCounts.publicPlans });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t, userId]);

  // The route's `vibeStatus`/`chatId` params are only ever populated by
  // in-app callers (chat, plan requests) that already computed them from
  // their own local data — a profile opened via a QR code or shared link
  // only ever carries `userId`, so it would otherwise always show the
  // "no relationship" state even when one already exists. Re-derive the
  // real relationship here (same lookup ChatScreen's "People here" sheet
  // uses) so the CTA is correct regardless of entry point.
  useEffect(() => {
    if (!currentUserId || currentUserId === userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const pairKey = getVibePairKey(currentUserId, userId);
        const relationshipSnap = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRelationships, pairKey)).catch(() => null);
        if (cancelled) return;
        // vibeRelationships is the single authoritative "are they Vibes"
        // source now — it covers both a normal request→accept pair AND a
        // DayVibe Aura Twin auto-match (functions/index.js), which never
        // had a vibeRequests doc at all. A legacy pair from before this
        // record existed falls through to the deterministic-id lookup
        // below until the one-time migration backfills it.
        if (relationshipSnap?.exists() && relationshipSnap.data()?.status === 'active') {
          setVibeStatus('connected');
          setResolvedChatId(String(relationshipSnap.data()?.activeChatId || ''));
          return;
        }

        const legacyChatDocId = pairKey;
        const [legacyChatSnap, sentSnap, receivedSnap] = await Promise.all([
          getDoc(doc(firestore, FIRESTORE_COLLECTIONS.privateChats, legacyChatDocId)).catch(() => null),
          getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, `${currentUserId}_${userId}`)).catch(() => null),
          getDoc(doc(firestore, FIRESTORE_COLLECTIONS.vibeRequests, `${userId}_${currentUserId}`)).catch(() => null),
        ]);
        if (cancelled) return;
        if (legacyChatSnap?.exists() && !legacyChatSnap.data()?.deletedFor?.[currentUserId]) {
          setVibeStatus('connected');
          setResolvedChatId(legacyChatDocId);
          return;
        }
        const sentStatus = sentSnap?.exists() ? sentSnap.data()?.status : null;
        const receivedStatus = receivedSnap?.exists() ? receivedSnap.data()?.status : null;
        if (sentStatus === 'accepted' || receivedStatus === 'accepted') {
          setVibeStatus('connected');
          setResolvedChatId(legacyChatDocId);
        } else if (sentStatus === 'pending' || receivedStatus === 'pending') {
          setVibeStatus('pending');
        } else {
          setVibeStatus('none');
        }
      } catch (error) {
        console.warn('[Tonyte][RN][PublicProfile] vibe status lookup failed', { userId, error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, userId]);

  const goBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const displayName = getUserDisplayName(profile);
  const profileName = displayName === 'User' && routeName ? routeName : displayName;
  const coverPhoto = profile?.coverPhoto || DEFAULT_COVER;
  const location = profile?.location || profile?.city || t('publicProfile.community');
  const interests = profile?.interests ?? [];
  const sharedInterests = routeSharedInterests ?? getSharedInterests(ownProfile?.interests, interests);
  // Only a real Day Vibe score for this exact pair; hidden when there is none.
  const vibeScore = usePairVibeGameScore(currentUserId, userId);
  const canTakeSafetyAction = Boolean(currentUserId && currentUserId !== userId);

  const submitReport = useCallback(async () => {
    if (!reason || !currentUserId) return;
    setReporting(true);
    try {
      await addDoc(collection(firestore, 'reports_users'), {
        reportedUserId: userId,
        reportedByUserId: currentUserId,
        chatId,
        reason,
        details: details.trim(),
        createdAt: serverTimestamp(),
        status: 'open',
      });
      setReportVisible(false);
      setReason('');
      setDetails('');
      showMessage({ message: t('publicProfile.reportSubmitted'), type: 'success' });
    } catch (error) {
      console.warn('[Tonyte][RN][Report] public profile report failed', { userId, error });
      Alert.alert(t('publicProfile.reportFailed'), t('common.tryAgain'));
    } finally {
      setReporting(false);
    }
  }, [chatId, currentUserId, details, reason, t, userId]);

  const confirmBlock = useCallback(() => {
    if (!currentUserId || currentUserId === userId || blocking) return;
    Alert.alert(
      t('publicProfile.blockTitle'),
      t('publicProfile.blockMessage').replace('{name}', profileName),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('publicProfile.block'),
          style: 'destructive',
          onPress: async () => {
            setBlocking(true);
            try {
              await blockUserFromPublicProfile(currentUserId, userId);
              showMessage({ message: t('publicProfile.userBlocked'), type: 'success' });
              navigation.goBack();
            } catch (error) {
              console.warn('[Tonyte][RN][Block] public profile block failed', {
                path: getBlockedUserPath(currentUserId, userId),
                currentUserId,
                targetUserId: userId,
                error,
              });
              Alert.alert(t('publicProfile.blockFailed'), t('common.tryAgain'));
            } finally {
              setBlocking(false);
            }
          },
        },
      ],
    );
  }, [blocking, currentUserId, navigation, profileName, t, userId]);

  const handleVibeAction = useCallback(async () => {
    if (!currentUserId || vibeBusy) return;
    if (vibeStatus === 'connected' && resolvedChatId) {
      navigation.navigate('ChatDetail', {
        chatId: resolvedChatId,
        name: profileName,
        kind: 'vibe',
        photo: profile?.profileImage,
        participantIds: [currentUserId, userId],
      });
      return;
    }
    if (vibeStatus === 'connected' || vibeStatus === 'pending') return;
    setVibeBusy(true);
    try {
      await sendVibeRequest({ fromUserId: currentUserId, toUserId: userId });
      setVibeStatus('pending');
      showMessage({ message: t('publicProfile.vibeSent').replace('{name}', profileName), type: 'success' });
    } catch (error) {
      showMessage({ message: error instanceof Error ? error.message : t('publicProfile.vibeSendFailed'), type: 'danger' });
    } finally {
      setVibeBusy(false);
    }
  }, [resolvedChatId, currentUserId, navigation, profile?.profileImage, profileName, t, userId, vibeBusy, vibeStatus]);

  const handleUnvibe = useCallback(async () => {
    if (!currentUserId || vibeBusy) return;
    setVibeBusy(true);
    try {
      // Backend-authoritative — the Cloud Function derives the pair's real
      // relationship/chat itself from (currentUserId, userId); it never
      // trusts a client-supplied chatId. See useVibes.ts / functions'
      // unvibeUserHandler.
      await unvibeUsers(userId);
      setVibeStatus('none');
      setResolvedChatId('');
      setUnvibeVisible(false);
      showMessage({ message: t('publicProfile.noLongerVibing').replace('{name}', profileName), type: 'default' });
    } catch {
      showMessage({ message: t('publicProfile.unvibeFailed'), type: 'danger' });
    } finally {
      setVibeBusy(false);
    }
  }, [currentUserId, profileName, t, userId, vibeBusy]);

  if (loading) {
    return (
      <View style={[styles.loadingRoot, { paddingTop: insets.top }]}>
        <ActivityIndicator color={Colors.sunset} />
      </View>
    );
  }

  if (profileUnavailable) {
    return (
      <View style={[styles.loadingRoot, { paddingTop: insets.top, paddingHorizontal: Spacing.xl }]}>
        <TouchableOpacity style={[styles.backBtn, { position: 'absolute', top: insets.top + 12, left: Spacing.base }]} onPress={goBack} activeOpacity={0.82}>
          <Icon name="arrow-left" size={20} color="#fff" />
        </TouchableOpacity>
        <Icon name="user-x" size={32} color="rgba(255,255,255,0.4)" />
        <Text style={styles.unavailableTitle}>{t('publicProfile.profileUnavailableTitle')}</Text>
        <Text style={styles.unavailableBody}>{t('publicProfile.profileUnavailableBody')}</Text>
        <TouchableOpacity style={styles.unavailableBackBtn} onPress={goBack} activeOpacity={0.82}>
          <Text style={styles.unavailableBackBtnText}>{t('common.goBack')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: insets.bottom + 36 }}>
        <ImageBackground source={{ uri: coverPhoto }} style={styles.cover} resizeMode="cover">
          <LinearGradient
            colors={['rgba(2,5,12,0.12)', 'rgba(2,5,12,0.40)', Colors.bg]}
            locations={[0, 0.48, 1]}
            style={StyleSheet.absoluteFill}
          />
          <TouchableOpacity style={[styles.backBtn, { top: insets.top + 12 }]} onPress={goBack} activeOpacity={0.82}>
            <Icon name="arrow-left" size={20} color="#fff" />
          </TouchableOpacity>
        </ImageBackground>

        <View style={styles.content}>
          <View style={styles.identityBlock}>
            <LinearGradient
              colors={['rgba(38,208,206,0.55)', 'rgba(242,125,38,0.48)']}
              style={styles.avatarFrame}
              accessible
              accessibilityLabel={profile?.faceVerified === true ? `${profileName}, Vibe Verified` : profileName}
            >
              <ProfileImage uri={profile?.profileImage} style={styles.avatar} />
              <VibeVerifiedBadge verified={profile?.faceVerified === true} size={112} />
              <AuraProfileBadge aura={todaysAura} compact />
            </LinearGradient>
            <View style={styles.nameAuraRow}>
              <Text style={styles.name}>{profileName}</Text>
            </View>
            {profile?.faceVerified ? (
              <View style={styles.faceVerifiedBadge}>
                <Icon name="user-check" size={12} color={Colors.turquoise} />
                <Text style={styles.faceVerifiedBadgeText}>{t('profile.vibeVerified')}</Text>
                <View style={styles.faceVerifiedCheck}>
                  <Icon name="check" size={9} color="#07101d" />
                </View>
              </View>
            ) : null}
            {profile?.username ? <Text style={styles.username}>@{profile.username}</Text> : null}
            <View style={styles.locationRow}>
              <Icon name="map-pin" size={13} color={Colors.turquoise} />
              <Text style={styles.locationText} numberOfLines={1}>{location}</Text>
            </View>
          </View>

          <View style={styles.statsRow}>
            <View style={styles.statBox}>
              <Text style={styles.statValue}>{counts.eventsCount}</Text>
              <Text style={styles.statLabel}>{t('profile.events')}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statValue}>{counts.plansCount}</Text>
              <Text style={styles.statLabel}>{t('profile.plans')}</Text>
            </View>
          </View>

          {canTakeSafetyAction ? (
            <View style={styles.vibePanel}>
              {vibeScore !== null ? (
                <View style={styles.vibeScoreRow}>
                  <View>
                    <Text style={styles.vibePanelLabel}>{t('publicProfile.vibeScore')}</Text>
                    <Text style={styles.vibePanelScore}>{Math.min(vibeScore, 99)}{t('publicProfile.match')}</Text>
                  </View>
                  <Icon name="zap" size={22} color={Colors.sunset} />
                </View>
              ) : null}
              {profile?.university ? (
                <View style={styles.universityRow}>
                  <Icon name="book-open" size={14} color={Colors.turquoise} />
                  <Text style={styles.universityText}>{profile.university}</Text>
                </View>
              ) : null}
              {sharedInterests.length ? (
                <View style={styles.interestWrap}>
                  {sharedInterests.slice(0, 5).map(item => (
                    <View key={item} style={styles.sharedInterestPill}>
                      <Text style={styles.sharedInterestText}>{BUILT_IN_INTEREST_KEYS.has(item.toLowerCase()) ? t(getInterestTranslationKey(item)) : item}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
              <View style={styles.vibeActionRow}>
                <TouchableOpacity style={styles.vibePrimaryBtn} onPress={handleVibeAction} disabled={vibeBusy || vibeStatus === 'pending'}>
                  <Icon name={vibeStatus === 'connected' ? 'message-circle' : 'zap'} size={15} color="#fff" />
                  <Text style={styles.vibePrimaryText}>
                    {vibeStatus === 'connected' ? t('publicProfile.message') : vibeStatus === 'pending' ? t('publicProfile.vibeSentStatus') : t('publicProfile.sendVibe')}
                  </Text>
                </TouchableOpacity>
                {vibeStatus === 'connected' ? (
                  <TouchableOpacity style={styles.unvibeBtn} onPress={() => setUnvibeVisible(true)}>
                    <Text style={styles.unvibeText}>{t('publicProfile.unvibe')}</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
          ) : null}

          {canTakeSafetyAction ? (
            <View style={styles.actionRow}>
              <TouchableOpacity style={styles.reportBtn} onPress={() => setReportVisible(true)} activeOpacity={0.82}>
                <Icon name="flag" size={15} color="rgba(255,255,255,0.82)" />
                <Text style={styles.actionText}>{t('publicProfile.report')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.blockBtn} onPress={confirmBlock} disabled={blocking} activeOpacity={0.82}>
                <Icon name="slash" size={15} color="#F87171" />
                <Text style={styles.blockText}>{blocking ? t('publicProfile.blocking') : t('publicProfile.block')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {profile?.bio ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('publicProfile.about')}</Text>
              <Text style={styles.bodyText}>{profile.bio}</Text>
            </View>
          ) : null}

          {interests.length ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('profile.interests')}</Text>
              <View style={styles.interestWrap}>
                {interests.map((interest, index) => (
                  <View key={`${interest}-${index}`} style={styles.interestPill}>
                    <Text style={styles.interestText}>{BUILT_IN_INTEREST_KEYS.has(interest.toLowerCase()) ? t(getInterestTranslationKey(interest)) : interest}</Text>
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          {publicActivities.plans.length ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('publicProfile.publicPlans')}</Text>
              {publicActivities.plans.map(item => (
                <TouchableOpacity key={item.id} style={styles.activityRow} onPress={() => {
                  trackEventDetailOpen({ eventId: item.id, source: ANALYTICS_SOURCES.UNKNOWN });
                  navigation.navigate('EventDetail', { eventId: item.id });
                }}>
                  <Icon name="calendar" size={14} color={Colors.sunset} />
                  <Text style={styles.activityTitle}>{item.title}</Text>
                  <Icon name="chevron-right" size={14} color="rgba(255,255,255,0.34)" />
                </TouchableOpacity>
              ))}
            </View>
          ) : null}

          {publicActivities.events.length ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('publicProfile.publicEvents')}</Text>
              {publicActivities.events.map(item => (
                <TouchableOpacity key={item.id} style={styles.activityRow} onPress={() => {
                  trackEventDetailOpen({ eventId: item.id, source: ANALYTICS_SOURCES.UNKNOWN });
                  navigation.navigate('EventDetail', { eventId: item.id });
                }}>
                  <Icon name="map-pin" size={14} color={Colors.turquoise} />
                  <Text style={styles.activityTitle}>{item.title}</Text>
                  <Icon name="chevron-right" size={14} color="rgba(255,255,255,0.34)" />
                </TouchableOpacity>
              ))}
            </View>
          ) : null}

          <View style={styles.safetyNote}>
            <Icon name="shield" size={16} color={Colors.turquoise} />
            <Text style={styles.safetyText}>{t('publicProfile.safetyHint')}</Text>
          </View>
        </View>
      </ScrollView>

      <Modal visible={reportVisible} transparent animationType="slide" onRequestClose={() => setReportVisible(false)} statusBarTranslucent>
        <View style={styles.modalOverlay}>
          <View style={[styles.reportSheet, { paddingBottom: insets.bottom + Spacing.base }]}>
            <View style={styles.reportHeader}>
              <View>
                <Text style={styles.reportKicker}>{t('publicProfile.safety')}</Text>
                <Text style={styles.reportTitle}>{t('publicProfile.reportPerson').replace('{name}', profileName)}</Text>
              </View>
              <TouchableOpacity style={styles.closeBtn} onPress={() => setReportVisible(false)} disabled={reporting}>
                <Icon name="x" size={18} color="#fff" />
              </TouchableOpacity>
            </View>
            <View style={styles.reasonList}>
              {REPORT_REASONS.map(item => (
                <TouchableOpacity
                  key={item.id}
                  style={[styles.reasonBtn, reason === item.id && styles.reasonSelected]}
                  onPress={() => setReason(item.id)}
                  activeOpacity={0.82}
                >
                  <Text style={[styles.reasonText, reason === item.id && styles.reasonSelectedText]}>{t(item.key)}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              style={styles.detailsInput}
              value={details}
              onChangeText={setDetails}
              placeholder={t('publicProfile.optionalDetails')}
              placeholderTextColor="rgba(255,255,255,0.30)"
              multiline
              textAlignVertical="top"
            />
            <TouchableOpacity
              style={[styles.submitReportBtn, (!reason || reporting) && styles.submitDisabled]}
              onPress={submitReport}
              disabled={!reason || reporting}
              activeOpacity={0.82}
            >
              <Text style={styles.submitReportText}>{reporting ? t('publicProfile.sending') : t('publicProfile.submitReport')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal visible={unvibeVisible} transparent animationType="fade" onRequestClose={() => setUnvibeVisible(false)}>
        <Pressable style={styles.unvibeOverlay} onPress={() => setUnvibeVisible(false)}>
          <Pressable style={styles.unvibeModal} onPress={() => undefined}>
            <Icon name="user-minus" size={24} color="#FB7185" />
            <Text style={styles.unvibeModalTitle}>{t('publicProfile.unvibeWith').replace('{name}', profileName)}</Text>
            <Text style={styles.unvibeModalText}>{t('publicProfile.unvibeBody')}</Text>
            <View style={styles.unvibeModalActions}>
              <TouchableOpacity style={styles.unvibeModalCancel} onPress={() => setUnvibeVisible(false)}>
                <Text style={styles.unvibeModalCancelText}>{t('publicProfile.keepVibe')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.unvibeModalConfirm} onPress={handleUnvibe} disabled={vibeBusy}>
                <Text style={styles.unvibeModalConfirmText}>{vibeBusy ? t('publicProfile.removing') : t('publicProfile.unvibe')}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.bg },
  loadingRoot: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.bg },
  unavailableTitle: { marginTop: 16, color: Colors.textPrimary, fontSize: Typography.lg, fontWeight: Typography.extrabold, textAlign: 'center' },
  unavailableBody: { marginTop: 8, color: 'rgba(255,255,255,0.52)', fontSize: Typography.sm, textAlign: 'center', maxWidth: 300 },
  unavailableBackBtn: { marginTop: 24, minHeight: 46, paddingHorizontal: 24, borderRadius: Radius.lg, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)' },
  unavailableBackBtnText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.bold },
  cover: { height: 280, justifyContent: 'flex-end' },
  backBtn: {
    position: 'absolute',
    left: Spacing.base,
    width: 44,
    height: 44,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  content: {
    paddingHorizontal: Spacing.base,
    marginTop: -58,
    gap: Spacing.base,
  },
  identityBlock: {
    alignItems: 'center',
    borderRadius: 26,
    padding: Spacing.xl,
    backgroundColor: 'rgba(9,15,26,0.88)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    ...Shadow.card,
  },
  avatarFrame: {
    position: 'relative',
    width: 112,
    height: 112,
    borderRadius: 40,
    padding: 2,
    marginBottom: Spacing.base,
  },
  avatar: { width: 108, height: 108, borderRadius: 38, overflow: 'hidden' },
  profileImage: { borderRadius: 38 },
  name: { color: Colors.textPrimary, fontSize: 26, lineHeight: 31, fontWeight: Typography.extrabold, textAlign: 'center', flexShrink: 1, width: '100%' },
  nameAuraRow: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    paddingHorizontal: 16,
  },
  username: { color: 'rgba(255,255,255,0.48)', fontSize: Typography.sm, marginTop: 4 },
  faceVerifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.35)',
    backgroundColor: 'rgba(38,208,206,0.10)',
  },
  faceVerifiedBadgeText: {
    color: Colors.turquoise,
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  faceVerifiedCheck: {
    width: 13,
    height: 13,
    borderRadius: 7,
    backgroundColor: Colors.turquoise,
    alignItems: 'center',
    justifyContent: 'center',
  },
  locationRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 12 },
  locationText: { color: 'rgba(255,255,255,0.68)', fontSize: Typography.sm, fontWeight: Typography.semibold },
  statsRow: { flexDirection: 'row', gap: Spacing.sm },
  vibePanel: {
    borderRadius: Radius.xl,
    padding: Spacing.base,
    gap: 12,
    backgroundColor: 'rgba(255,122,0,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,122,0,0.2)',
  },
  vibeScoreRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  vibePanelLabel: { color: 'rgba(255,255,255,0.4)', fontSize: 9, fontWeight: Typography.bold, letterSpacing: 1.5 },
  vibePanelScore: { marginTop: 3, color: '#fff', fontSize: Typography.lg, fontWeight: Typography.extrabold },
  universityRow: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  universityText: { color: 'rgba(255,255,255,0.68)', fontSize: Typography.sm },
  sharedInterestPill: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: Radius.pill, backgroundColor: 'rgba(255,122,0,0.1)' },
  sharedInterestText: { color: '#FDBA74', fontSize: Typography.xs, fontWeight: Typography.semibold },
  vibeActionRow: { flexDirection: 'row', gap: 8 },
  vibePrimaryBtn: { flex: 1, minHeight: 46, borderRadius: Radius.lg, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: '#FF7A00' },
  vibePrimaryText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.bold },
  unvibeBtn: { minHeight: 46, paddingHorizontal: 16, borderRadius: Radius.lg, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(251,113,133,0.09)' },
  unvibeText: { color: '#FB7185', fontSize: Typography.sm, fontWeight: Typography.bold },
  statBox: {
    flex: 1,
    borderRadius: Radius.lg,
    paddingVertical: Spacing.base,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.055)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  statValue: { color: Colors.textPrimary, fontSize: Typography.xl, fontWeight: Typography.extrabold },
  statLabel: { color: 'rgba(255,255,255,0.45)', fontSize: Typography.xs, marginTop: 2 },
  actionRow: { flexDirection: 'row', gap: Spacing.sm },
  reportBtn: {
    flex: 1,
    minHeight: 48,
    borderRadius: Radius.lg,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  blockBtn: {
    flex: 1,
    minHeight: 48,
    borderRadius: Radius.lg,
    backgroundColor: 'rgba(248,113,113,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.28)',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  actionText: { color: 'rgba(255,255,255,0.82)', fontSize: Typography.sm, fontWeight: Typography.bold },
  blockText: { color: '#F87171', fontSize: Typography.sm, fontWeight: Typography.bold },
  section: {
    borderRadius: Radius.xl,
    padding: Spacing.base,
    backgroundColor: 'rgba(255,255,255,0.045)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  sectionTitle: { color: Colors.textPrimary, fontSize: Typography.base, fontWeight: Typography.bold, marginBottom: Spacing.sm },
  bodyText: { color: 'rgba(255,255,255,0.66)', fontSize: Typography.sm, lineHeight: 21 },
  interestWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  interestPill: {
    borderRadius: Radius.pill,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: 'rgba(38,208,206,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.22)',
  },
  interestText: { color: 'rgba(255,255,255,0.78)', fontSize: Typography.xs, fontWeight: Typography.semibold },
  activityRow: { minHeight: 42, flexDirection: 'row', alignItems: 'center', gap: 9, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: 'rgba(255,255,255,0.08)' },
  activityTitle: { flex: 1, color: 'rgba(255,255,255,0.78)', fontSize: Typography.sm, fontWeight: Typography.semibold },
  safetyNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    padding: Spacing.base,
    backgroundColor: 'rgba(38,208,206,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.14)',
  },
  safetyText: { flex: 1, color: 'rgba(255,255,255,0.52)', fontSize: Typography.xs, lineHeight: 17 },
  modalOverlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.62)' },
  reportSheet: {
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    padding: Spacing.base,
    gap: Spacing.base,
    backgroundColor: 'rgba(10,15,25,0.98)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  reportHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: Spacing.base },
  reportKicker: { color: 'rgba(255,255,255,0.42)', fontSize: 11, fontWeight: Typography.bold, letterSpacing: 1.6 },
  reportTitle: { color: Colors.textPrimary, fontSize: Typography.lg, fontWeight: Typography.bold, marginTop: 4 },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  reasonList: { gap: 8 },
  reasonBtn: {
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.base,
    paddingVertical: 11,
    backgroundColor: 'rgba(255,255,255,0.055)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  reasonSelected: { backgroundColor: Colors.sunset, borderColor: Colors.sunset },
  reasonText: { color: 'rgba(255,255,255,0.78)', fontSize: Typography.sm, fontWeight: Typography.semibold },
  reasonSelectedText: { color: '#fff' },
  detailsInput: {
    height: 92,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.base,
    paddingVertical: 12,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    backgroundColor: 'rgba(255,255,255,0.055)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
  },
  submitReportBtn: {
    minHeight: 48,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.sunset,
  },
  submitDisabled: { opacity: 0.55 },
  submitReportText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.bold },
  unvibeOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, backgroundColor: 'rgba(0,0,0,0.72)' },
  unvibeModal: { width: '100%', maxWidth: 380, alignItems: 'center', padding: 22, borderRadius: 26, backgroundColor: '#0D131E', borderWidth: 1, borderColor: 'rgba(251,113,133,0.22)' },
  unvibeModalTitle: { marginTop: 12, color: '#fff', fontSize: Typography.lg, fontWeight: Typography.extrabold, textAlign: 'center' },
  unvibeModalText: { marginTop: 7, color: 'rgba(255,255,255,0.52)', fontSize: Typography.sm, textAlign: 'center' },
  unvibeModalActions: { width: '100%', flexDirection: 'row', gap: 10, marginTop: 20 },
  unvibeModalCancel: { flex: 1, minHeight: 46, borderRadius: Radius.lg, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.07)' },
  unvibeModalCancelText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.bold },
  unvibeModalConfirm: { flex: 1, minHeight: 46, borderRadius: Radius.lg, alignItems: 'center', justifyContent: 'center', backgroundColor: '#E11D48' },
  unvibeModalConfirmText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.bold },
});

export default PublicUserProfileScreen;
